import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { faTelegram } from '@fortawesome/free-brands-svg-icons';
import InfoTooltip from './InfoTooltip';
import SlackLogo from './SlackLogo';
import './AuthVisual.css';

/**
 * General brand visual beside an auth form (login / register). A grid-lit purple
 * panel with a rotating carousel of real things the agent can DO, and a marquee,
 * pinned to the bottom, of everything it connects to. Generalized sibling of
 * WixAuthVisual (no Wix-specific content), so it fits any onboarding surface.
 *
 * Each carousel card maps to a shipped capability; copy lives under av_* i18n
 * keys (canonical English in en.json; other locales fall back). A card labels
 * itself with a category (`app`, translated) or, where the tool IS the message
 * (Zapier / Make / n8n / Stripe), with the tool's own name (`appName`, a proper
 * noun, so it stays a literal). The named tools are spread across the rotation
 * rather than clustered, so each one gets its own beat.
 *
 * The three automation cards are grounded in
 * ../../../ultimo-bots-ai-docs/skill-flow-builder/13-automation-platforms.md:
 * Zapier is send-only (its webhook answers with a fixed acknowledgment), Make
 * and n8n can return workflow data the agent uses in its reply. Do not promise
 * Zapier data-back.
 */
const ACTIONS = [
  { logo: '/zapier_logo.png', appName: 'Zapier', title: 'av_act_zapier_title', desc: 'av_act_zapier_desc' },
  { logo: '/calendly-logo.png', app: 'av_cat_scheduling', title: 'av_act_book_title', desc: 'av_act_book_desc' },
  { logo: '/make-logo.png', appName: 'Make', title: 'av_act_make_title', desc: 'av_act_make_desc' },
  { logo: '/stripe-logo.png', appName: 'Stripe', title: 'av_act_stripe_title', desc: 'av_act_stripe_desc' },
  { logo: '/n8n-logo.png', appName: 'n8n', bleed: true, title: 'av_act_n8n_title', desc: 'av_act_n8n_desc' },
  { logo: '/notion-logo.png', app: 'av_cat_knowledge', title: 'av_act_knowledge_title', desc: 'av_act_knowledge_desc' },
  { logo: '/hubspot-logo.png', app: 'av_cat_leads', title: 'av_act_leads_title', desc: 'av_act_leads_desc' },
  { logo: '/instagram-logo.webp', app: 'av_cat_channels', title: 'av_act_channels_title', desc: 'av_act_channels_desc' },
];

// Everything the agent can connect to, ordered in groups so the marquee reads as
// scheduling → payments → CRM & support → automation → knowledge → channels
// rather than a random pile. Brand names are proper nouns, so they stay as
// literals. A logo is either an image (`src`), an inline brand SVG (`node`), or
// a FontAwesome brand glyph (`faIcon` + `color`) — Slack and Telegram reuse the
// portal's canonical marks so every surface shows the same thing. `bleed` marks
// a logo that IS a full square (its own background), so it fills the tile
// instead of sitting shrunken on white.
const TOOLS = [
  // scheduling
  { src: '/calendly-logo.png', name: 'Calendly' },
  { src: '/calcom-logo.png', name: 'Cal.com' },
  // payments
  { src: '/stripe-logo.png', name: 'Stripe' },
  // CRM, marketing & support
  { src: '/hubspot-logo.png', name: 'HubSpot' },
  { src: '/mailchimp-logo.png', name: 'Mailchimp' },
  { src: '/zendesk-logo.png', name: 'Zendesk' },
  // automation platforms
  { src: '/zapier_logo.png', name: 'Zapier' },
  { src: '/make-logo.png', name: 'Make' },
  { src: '/n8n-logo.png', name: 'n8n', bleed: true },
  // knowledge sources
  { src: '/google-drive.png', name: 'Google Drive' },
  { src: '/onedrive.png', name: 'OneDrive' },
  { src: '/notion-logo.png', name: 'Notion' },
  // channels
  { src: '/facebook-logo.webp', name: 'Facebook' },
  { src: '/instagram-logo.webp', name: 'Instagram' },
  { src: '/whatsapp-logo.webp', name: 'WhatsApp' },
  { faIcon: faTelegram, color: '#229ED9', name: 'Telegram' },
  { node: <SlackLogo size={21} />, name: 'Slack' },
];

const ROTATE_MS = 3400;

// The tile itself carries the accessible name, so the mark inside is decorative.
function toolMark(tool) {
  if (tool.node) return tool.node;
  if (tool.faIcon) return <FontAwesomeIcon icon={tool.faIcon} style={{ color: tool.color, fontSize: 21 }} />;
  return <img src={tool.src} alt="" />;
}

export default function AuthVisual() {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = ACTIONS.length;
  const go = useCallback((d) => setIdx((i) => (i + d + n) % n), [n]);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setIdx((i) => (i + 1) % n), ROTATE_MS);
    return () => clearInterval(id);
  }, [paused, n]);

  const a = ACTIONS[idx];

  return (
    <div className="av" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="av-grid" aria-hidden="true" />
      <div className="av-glow" aria-hidden="true" />
      <div className="av-inner">
        <div className="av-top">
          <span className="av-eyebrow">{t('av_eyebrow')}</span>
          <h3 className="av-title">{t('av_title')}</h3>
          <p className="av-sub">{t('av_sub')}</p>

          <div className="av-carousel">
            <div className="av-action" key={idx}>
              <div className="av-action-head">
                <span className={`av-action-logo ${a.bleed ? 'av-bleed' : ''}`}>
                  <img src={a.logo} alt="" />
                </span>
                <span className="av-action-app">{a.appName || t(a.app)}</span>
                <span className="av-action-dot" aria-hidden="true" />
              </div>
              <span className="av-action-title">{t(a.title)}</span>
              <span className="av-action-desc">{t(a.desc)}</span>
            </div>

            <div className="av-controls">
              <button type="button" className="av-arrow" onClick={() => go(-1)} aria-label={t('av_prev')}>
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <div className="av-dots">
                {ACTIONS.map((act, i) => (
                  <button type="button" key={act.title} className={`av-dot ${i === idx ? 'av-on' : ''}`}
                    onClick={() => setIdx(i)} aria-label={String(i + 1)} />
                ))}
              </div>
              <button type="button" className="av-arrow" onClick={() => go(1)} aria-label={t('av_next')}>
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        </div>

        <div className="av-more">
          <span className="av-more-label">{t('av_more')}</span>
          <div className="av-marquee">
            <div className="av-marquee-track">
              {[...TOOLS, ...TOOLS].map((tool, i) => (
                <InfoTooltip key={i} text={tool.name} placement="top" compact>
                  <span className={`av-more-logo ${tool.bleed ? 'av-bleed' : ''}`}
                    role="img" aria-label={tool.name}>
                    {toolMark(tool)}
                  </span>
                </InfoTooltip>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
